import { VStack } from "@/components/layout/VStack";
import { DashboardCard } from "@/components/ui/DashboardCard/DashboardCard";
import type { MissingEnrollmentData } from "@/artifacts/admin/pages/Dashboard";
import { getMissingEnrollmentTotals } from "@/artifacts/admin/utils/dashboard/getMissingEnrollmentTotals";
import { HStack } from "@/components/layout/HStack";
import { Button } from "@/components/ui/Button/Button";
import { Eye, EyeClosed, UsersRound } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { MissingEnrollmentsTable } from "./MissingEnrollmentDataTable";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/Empty/Empty";
import { Skeleton } from "@/components/ui/Skeleton/Skeleton";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/Collapsible/Collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/Tooltip/Tooltip";
import { format } from "date-fns";
import { useSearchParams } from "react-router-dom";

const MissingEnrollment = ({
  data,
  loading,
  onOpen,
}: {
  data: MissingEnrollmentData[] | null;
  loading: boolean;
  onOpen: (studentId: string) => void;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState<boolean>(true);

  const selectedRotationId = useMemo(() => {
    const rotationId = searchParams.get("mERot") ?? "";
    if (!rotationId) return null;
    return rotationId;
  }, [searchParams]);

  const setSelectedRotationId = (next: string | null) => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);

      if (!next) {
        sp.delete("mERot");
        return sp;
      }

      sp.set("mERot", next);
      return sp;
    });
  };

  const selectedRotation = selectedRotationId
    ? (data?.find((r) => r._id === selectedRotationId) ?? null)
    : null;

  useEffect(() => {
  }, [data]);

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      style={{ marginTop: "-8px" }}
    >
      <VStack gap="2rem">
        <VStack gap={0}>
          <HStack>
            <h3
              style={{
                fontSize: "var(--text-lg)",
                lineHeight: "var(--line-height-lg)",
                fontWeight: "var(--font-bold)",
              }}
            >
              Missing Enrollment
            </h3>
            <Tooltip>
              <TooltipTrigger asChild>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="icon-small">
                    {open ? <EyeClosed /> : <Eye />}
                  </Button>
                </CollapsibleTrigger>
              </TooltipTrigger>
              <TooltipContent>
                {open ? "Hide" : "Show"} missing enrollment section.
              </TooltipContent>
            </Tooltip>
          </HStack>
          <p style={{ color: "var(--muted-foreground)" }}>
            Students missing enrollment and preferences for open sessions
          </p>
        </VStack>
        <CollapsibleContent>
          <VStack gap="1rem">
            <HStack gap="1rem" style={{ flexWrap: "wrap" }}>
              {loading ? (
                <Skeleton style={{ height: "170px", width: "100%" }} />
              ) : !data || data.length < 1 ? (
                <Empty style={{ borderColor: "var(--secondary-border)" }}>
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <UsersRound />
                    </EmptyMedia>
                    <EmptyTitle>No Students Missing Enrollment</EmptyTitle>
                    <EmptyDescription>
                      There are no students missing enrollment. If you believe
                      this is an error, please refresh.
                    </EmptyDescription>
                  </EmptyHeader>
                </Empty>
              ) : (
                data.map((rotation) => {
                  const totals = getMissingEnrollmentTotals(rotation);

                  return (
                    <DashboardCard
                      key={rotation._id}
                      gap="1rem"
                      style={{
                        flex: "1 1 calc(20% - 1rem)",
                        flexWrap: "nowrap",
                        textWrap: "nowrap",
                      }}
                    >
                      <h4
                        style={{
                          fontSize: "var(--text-md)",
                          lineHeight: "var(--line-height-md)",
                          fontWeight: "var(--font-bold)",
                        }}
                      >
                        {format(rotation.date, "MMMM dd, yyyy")}
                      </h4>
                      <VStack gap={0}>
                        <div>
                          <strong
                            style={{
                              fontSize: "var(--text-md)",
                              lineHeight: "var(--line-height-md)",
                            }}
                          >
                            {totals.missingEnrollments}
                          </strong>{" "}
                          <span
                            style={{
                              color: "var(--muted-foreground)",
                            }}
                          >
                            missing enrollments
                          </span>
                        </div>
                        <div>
                          <strong
                            style={{
                              fontSize: "var(--text-md)",
                              lineHeight: "var(--line-height-md)",
                            }}
                          >
                            {totals.missingPreferences}
                          </strong>{" "}
                          <span
                            style={{
                              color: "var(--muted-foreground)",
                            }}
                          >
                            missing preferences
                          </span>
                        </div>
                      </VStack>
                      {totals.missingEnrollments + totals.missingPreferences !==
                        0 && (
                        <Button
                          size="small"
                          variant={
                            selectedRotation?._id === rotation._id
                              ? "outline"
                              : "default"
                          }
                          onClick={() =>
                            setSelectedRotationId(
                              selectedRotation?._id === rotation._id
                                ? null
                                : rotation._id,
                            )
                          }
                        >
                          {selectedRotation?._id === rotation._id ? (
                            <>
                              <EyeClosed />
                              Hide Students
                            </>
                          ) : (
                            <>
                              <Eye />
                              View Students
                            </>
                          )}
                        </Button>
                      )}
                    </DashboardCard>
                  );
                })
              )}
            </HStack>
            {selectedRotation && (
              <MissingEnrollmentsTable
                sessions={selectedRotation.sessions}
                rotation={selectedRotation.date}
                onOpen={onOpen}
              />
            )}
          </VStack>
        </CollapsibleContent>
      </VStack>
    </Collapsible>
  );
};

export { MissingEnrollment };
